import axios from 'axios';
import type { InternalAxiosRequestConfig, AxiosError } from 'axios';
import { locales, defaultLocale } from '@/lib/config';

const request = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '',
  timeout: 10000,
});

const pendingRequests = new Map<string, AbortController>();

const generateRequestKey = (config: InternalAxiosRequestConfig): string => {
  const { url, method, params, data } = config;
  return `${method?.toUpperCase() || 'GET'}|${url}|${JSON.stringify(params)}|${JSON.stringify(data)}`;
};

const removePendingRequest = (config: InternalAxiosRequestConfig, cancel = false) => {
  const key = generateRequestKey(config);
  if (pendingRequests.has(key)) {
    if (cancel) pendingRequests.get(key)?.abort();
    pendingRequests.delete(key);
  }
};

const addPendingRequest = (config: InternalAxiosRequestConfig, controller: AbortController) => {
  const key = generateRequestKey(config);
  pendingRequests.set(key, controller);
};

// ✅ 修复：pathname 改为 window.location.pathname
export const getLangFromPath = (path?: string): string => {
  // 服务器端也能从传入的 path 参数获取语言
  if (typeof window !== 'undefined') {
    path = path || window.location.pathname;
  }
  if (!path) return defaultLocale;
  
  const localePattern = locales.join('|');
  const match = path.match(new RegExp(`^\\/(${localePattern})\\b`, 'i'));
  return match ? match[1].toLowerCase() : defaultLocale;
};

request.interceptors.request.use((config) => {
  const ctrl = new AbortController();
  removePendingRequest(config, true);
  addPendingRequest(config, ctrl);
  config.signal = ctrl.signal;

  // 客户端：从 window.location.pathname 获取
  if (typeof window !== 'undefined') {
    const lang = getLangFromPath();
    config.params = { ...config.params, lang };
  } else {
    // 服务器端：从 config.url 中提取语言前缀
    const fullUrl = config.url || '';
    const lang = getLangFromPath(fullUrl);
    config.params = { ...config.params, lang };
  }
  return config;
});

request.interceptors.response.use(
  (res) => {
    removePendingRequest(res.config);
    return res;
  },
  (err: AxiosError) => {
    if (err.config) removePendingRequest(err.config);
    return Promise.reject(err);
  }
);

export const cancelAllPendingRequests = () => {
  pendingRequests.forEach(ctrl => ctrl.abort());
  pendingRequests.clear();
};

export { request };
export default request;